import { Box, Button, Center, Flex, HStack, Image, Input, Link, Spinner, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GifPickerCard, GifPickerPill } from '../../types/ui-blocks';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { useBizyGifSearch, type BizyGif } from '../context/gif-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { ChevronUpOutline } from '../lib/icons';
import { useDebounceValue } from '../lib/use-debounce-value';
import type { BizyNodeProps } from './node-props';

const MAX_RESULTS_HEIGHT = 320;

// Even/odd split into two masonry-ish columns (replaces the app's `split` util).
function splitColumns(gifs: BizyGif[]): [BizyGif[], BizyGif[]] {
	const left: BizyGif[] = [];
	const right: BizyGif[] = [];
	gifs.forEach((gif, i) => (i % 2 === 0 ? left : right).push(gif));
	return [left, right];
}

/**
 * Standalone (browse-only) render of a `gif_picker_card` node. The interactive
 * picker is embedded in the recognition draft card via {@link GifPicker}; this
 * only runs if a picker node ever renders without its sibling draft card.
 */
export function GifPickerCardNode({ node }: BizyNodeProps) {
	const block = node as GifPickerCard;
	return <GifPicker pills={block.payload.pills} primaryQuery={block.payload.primaryQuery} />;
}

/**
 * Collapsed "Review Gifs" disclosure. Expanding reveals contextual category
 * pills + a search box; results stream from the injected {@link useBizyGifSearch}
 * in a two-column layout. Presentational: picking a gif calls `onSelectGif`.
 */
export function GifPicker({
	pills,
	primaryQuery,
	onSelectGif,
	isReadOnly = false,
}: {
	pills: GifPickerPill[];
	primaryQuery?: string | null;
	onSelectGif?: (gif: BizyGif) => void;
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { track } = useBizyAnalytics();
	const searchGifs = useBizyGifSearch();
	const disclosure = useDisclosure();
	const containerRef = useRef<HTMLDivElement>(null);

	const [inputValue, setInputValue] = useState(primaryQuery ?? pills[0]?.query ?? '');
	const query = useDebounceValue(inputValue, 300);

	const [gifs, setGifs] = useState<BizyGif[]>([]);
	const [provider, setProvider] = useState<{ name?: string; url?: string }>({});
	const [isLoading, setIsLoading] = useState(false);

	const canSelect = !isReadOnly && !!onSelectGif;

	// Lazy: only fetch once the picker is open; re-fetch on debounced query change.
	useEffect(() => {
		if (!disclosure.isOpen) return;
		let cancelled = false;
		setIsLoading(true);
		searchGifs(query)
			.then((result) => {
				if (cancelled) return;
				setGifs(result.gifs);
				setProvider({ name: result.providerName, url: result.providerUrl });
			})
			.catch(() => {
				if (!cancelled) setGifs([]);
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [disclosure.isOpen, query, searchGifs]);

	const [left, right] = splitColumns(gifs);

	const handleToggle = () => {
		const willOpen = !disclosure.isOpen;
		if (willOpen) {
			track(BizyAnalyticsEvent.GifPickerOpened, { path: window.location.pathname });
		}
		disclosure.onToggle();
		if (willOpen) {
			requestAnimationFrame(() => containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
		}
	};

	const handlePill = (pillQuery: string) => {
		setInputValue(pillQuery);
		track(BizyAnalyticsEvent.GifPillClicked, { query: pillQuery, path: window.location.pathname });
	};

	const handleSelect = (gif: BizyGif) => {
		if (!canSelect) return;
		onSelectGif?.(gif);
		track(BizyAnalyticsEvent.GifSelected, { path: window.location.pathname });
	};

	return (
		<Box ref={containerRef} mt={2} border="1px solid" borderColor="border.default" borderRadius="md" bg="bg.surface" overflow="hidden">
			<Flex as="button" type="button" w="100%" align="center" justify="space-between" px={3} py={2} onClick={handleToggle} aria-expanded={disclosure.isOpen}>
				<Text fontSize="sm" fontWeight="semibold">
					{t('uiBlocks.gifPicker.title')}
				</Text>
				<ChevronUpOutline transitionProperty="transform" transitionDuration="fast" transform={disclosure.isOpen ? 'none' : 'rotate(180deg)'} />
			</Flex>

			{disclosure.isOpen && (
				<Box px={3} pb={3} role="group">
					<Input
						size="sm"
						mb={2}
						type="text"
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						placeholder={t('uiBlocks.gifPicker.searchPlaceholder', { provider: provider.name ?? '' })}
					/>

					{pills.length > 0 && (
						<HStack spacing={2} mb={2} overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							{pills.map((pill, i) => (
								<Button key={`${pill.label}-${i}`} size="xs" colorScheme="gray" variant="ghost" flexShrink={0} onClick={() => handlePill(pill.query)}>
									{pill.label}
								</Button>
							))}
						</HStack>
					)}

					{isLoading ? (
						<Center h={`${MAX_RESULTS_HEIGHT}px`}>
							<Spinner />
						</Center>
					) : (
						<Flex gap={1} align="start" h={`${MAX_RESULTS_HEIGHT}px`} overflowY="auto" role="listbox">
							<GifColumn gifs={left} canSelect={canSelect} onSelect={handleSelect} />
							<GifColumn gifs={right} canSelect={canSelect} onSelect={handleSelect} />
						</Flex>
					)}

					{provider.name && (
						<Text fontSize="xs" color="text.softer" mt={2} textAlign="right">
							{t('uiBlocks.gifPicker.poweredBy')}{' '}
							<Link href={provider.url} isExternal fontWeight="semibold">
								{provider.name}
							</Link>
						</Text>
					)}
				</Box>
			)}
		</Box>
	);
}

function GifColumn({ gifs, canSelect, onSelect }: { gifs: BizyGif[]; canSelect: boolean; onSelect: (gif: BizyGif) => void }) {
	return (
		<VStack spacing={1} flex={1} minW={0}>
			{gifs.map((gif) => (
				<Box
					as="button"
					type="button"
					key={gif.id}
					role="option"
					w="100%"
					borderRadius="md"
					overflow="hidden"
					cursor={canSelect ? 'pointer' : 'default'}
					onClick={() => onSelect(gif)}
				>
					<Image alt={gif.description ?? ''} src={gif.preview} w="100%" />
				</Box>
			))}
		</VStack>
	);
}
